import type {
  Folder,
  IRecording,
  RecorderHealthCheck,
  RecordingTypes,
  Workspace,
} from '@carboncopyco/types';
import { Debug } from '@carboncopyco/utils';
import { workspaceAddAsset } from '../utils/queryDataUpdateHandler';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:recorderApi');

export interface RecorderTokenNewArgs {
  organizationId: string;
  firstUrl: string;
}

export interface RecorderNewArgs {
  organizationId: string;
  recorderUuid: string;
  firstUrl: string;
  recordName: string;
  description: string;
  recordingType?: RecordingTypes;
}

export interface RecordingDraftNewArgs {
  organizationId: string;
  recordingId: string;
  url: string;
}

export interface SaveRecordingDraftArgs {
  organizationId: string;
  draftId: string;
  comment: string;
}

export interface FinishRecordingArgs {
  organizationId: string;
  recordingId: number;
  recordName: string;
  description: string;
  workspaceId?: Workspace['id'];
  folderId?: Folder['id'];
}

export interface SaveRecordingPageArgs {
  organizationId: string;
  recordingId: string;
  url: string;
}

export interface SaveRecordingDraftPageArgs {
  organizationId: string;
  draftId: string;
  url: string;
}

const recorderApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    recorderNew: builder.mutation<IRecording, RecorderNewArgs>({
      query: ({
        organizationId,
        recorderUuid,
        recordName,
        description,
        firstUrl,
        recordingType = 'dynamic_recording',
      }) => ({
        url: `/api/${organizationId}/records/new`,
        method: 'PUT',
        body: {
          recorderUuid,
          recordName,
          description,
          firstUrl,
          recordingType,
        },
      }),
    }),

    recorderTokenNew: builder.mutation<IRecording, RecorderTokenNewArgs>({
      query: ({ organizationId, firstUrl }) => ({
        url: `/api/${organizationId}/d_token/new`,
        method: 'PUT',
        body: { firstUrl },
      }),
    }),

    recordingDraftNew: builder.mutation<IRecording, RecordingDraftNewArgs>({
      query: ({ organizationId, recordingId, url }) => ({
        url: `/api/${organizationId}/record/${recordingId}/drafts/new`,
        method: 'PUT',
        body: { url },
      }),
    }),

    recorderHealthCheck: builder.query<
      RecorderHealthCheck['data'],
      { domain: string }
    >({
      query: ({ domain }) => ({
        url: domain.replace('register', 'healthcheck'),
        method: 'GET',
      }),
    }),

    saveRecordingDraft: builder.mutation<IRecording, SaveRecordingDraftArgs>({
      query: ({ organizationId, draftId, comment }) => ({
        url: `/api/${organizationId}/draft/save/${draftId}`,
        method: 'POST',
        body: { comment },
      }),
      invalidatesTags: (_, __, { draftId }) => [
        { type: 'RecordingDraft', id: draftId },
      ],
    }),

    saveRecordingPage: builder.mutation<null, SaveRecordingPageArgs>({
      query: ({ organizationId, recordingId, url }) => ({
        url: `/api/${organizationId}/${recordingId}/dynamic-web-object`,
        method: 'POST',
        body: { url },
      }),
    }),

    saveRecordingDraftPage: builder.mutation<
      IRecording,
      SaveRecordingDraftPageArgs
    >({
      query: ({ organizationId, draftId, url }) => ({
        url: `/api/${organizationId}/record/draft/${draftId}/dynamic-web-object`,
        method: 'POST',
        body: { url },
      }),
    }),

    finishRecording: builder.mutation<IRecording, FinishRecordingArgs>({
      query: ({
        organizationId,
        recordingId,
        recordName,
        description,
        workspaceId,
        folderId,
      }) => ({
        url: `/api/${organizationId}/record/${recordingId}`,
        method: 'POST',
        body: {
          recordName,
          description,
          status: 'recording_finished',
          workspaceId,
          folderId,
        },
      }),
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey:
            arg?.folderId && arg.folderId !== '0' ? 'folderId' : 'workspaceId',
          entityType: 'record',
        });
      },
      invalidatesTags: (res) =>
        res
          ? [
              {
                type: res.folderId ? 'FolderAssets' : 'WorkspaceAssets',
                id: res.folderId || res.workspaceId,
              },
            ]
          : [{ type: 'Recording' }],
    }),
  }),
});

export const {
  useLazyRecorderHealthCheckQuery,
  useRecorderHealthCheckQuery,
  useSaveRecordingDraftMutation,
  useFinishRecordingMutation,
  useSaveRecordingPageMutation,
  useSaveRecordingDraftPageMutation,
  useRecordingDraftNewMutation,
  useRecorderNewMutation,
  useRecorderTokenNewMutation,
} = recorderApi;
